import type { SxProps, Theme } from '@mui/material';
import { ButtonBase, useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import type { FC } from 'react';

type Option = {
  id: string;
  label: string;
};

type Props = {
  value: string;
  items: Option[];
  onChange: (value: string) => void;
  disabled?: boolean;
  fitContent?: boolean;
  size?: 'medium' | 'large';
  sx?: SxProps<Theme>;
};

export const SegmentedControl: FC<Props> = ({
  sx,
  items,
  value,
  onChange,
  disabled,
  fitContent,
  size = 'medium',
}) => {
  const theme = useTheme();
  const isLarge = size === 'large';
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        width: fitContent ? 'fit-content' : undefined,
        borderRadius: '0.5rem',
        border: `1px solid ${theme.palette.SlabGray.Stroke}`,
        padding: isLarge ? '0.5rem' : '0.25rem',
        gap: isLarge ? '1rem' : undefined,
        ...sx,
      }}
    >
      {items.map((item) => {
        const isSelected = item.id === value;
        const onClick = (): void => {
          if (!isSelected) {
            onChange(item.id);
          }
        };
        let color: string;
        if (disabled) {
          color = theme.palette.SlabGray['500'];
        } else if (isSelected) {
          color = theme.palette.SlabIndigo['700'];
        } else {
          color = theme.palette.SlabGray['600'];
        }
        let backgroundColor: string | undefined;
        if (isSelected) {
          backgroundColor = theme.palette.SlabGray.Stroke;
        }
        let cursor: string;
        if (disabled) {
          cursor = 'default';
        } else {
          cursor = 'pointer';
        }
        return (
          <ButtonBase
            key={item.id}
            aria-pressed={isSelected}
            disabled={disabled}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flex: fitContent ? '0 0 auto' : 1,
              padding: fitContent ? '0.25rem 0.75rem' : '0.25rem 0',
              whiteSpace: fitContent ? 'nowrap' : undefined,
              font: 'inherit',
              fontSize: isLarge ? '1.125rem' : undefined,
              minHeight: isLarge ? '2.5rem' : undefined,
              backgroundColor,
              color,
              cursor,
              borderRadius: '0.5rem',
            }}
            onClick={onClick}
          >
            {item.label}
          </ButtonBase>
        );
      })}
    </Box>
  );
};
